import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, ElementRef, ViewChild, HostListener } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import * as L from 'leaflet';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';

import { PermitV2Service } from '../../services/permits.abstract.service';
import { PermitDepartmentService } from '../../services/permit-department.service';
import { PermitCategoryService } from '../../services/permit-category.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { BcitiConfirmDialogComponent } from '../../../global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import {
    AdminPermitDetail,
    PermitFile,
    permitFileName,
    isPermitFileImage,
    isPermitFilePdf,
    isPermitFileWord,
    isPermitFileExcel,
    safePermitFileUrl,
    PopulatedPermitTypeRef,
    PopulatedRef,
    UpdateFeePayload,
} from '../../models/permit.model';
import { displayUnitFor, FormFieldConfig } from '../../models/permit-type.model';
import { PermitDepartment, permitDepartmentErrorKey, permitDepartmentName } from '../../models/permit-department.model';
import { PermitCategory } from '../../models/permit-category.model';
import { SyncStatus } from '../../constants/permit.constants';
import { UserService } from '../../../user/service/user.abstract.service';
import { ProfileResponse } from '../../../user/user.model';
import { createLocationMarkerIcon } from '../../../global/utils/map/map-marker-icon.util';

/** Sentinel for the "Other" option in the add-file field selector — never sent as a fieldId. */
export const OTHER_DOCUMENT_FIELD = '__other__';

/** Mirrors the backend's MAX_FILES_PER_FIELD — how many files a file field can hold. */
export const MAX_FILES_PER_FIELD = 5;

@Component({
    selector: 'app-permit-info',
    templateUrl: './permit-info.component.html',
    styleUrls: ['./permit-info.component.scss'],
})
export class PermitInfoComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() permit: AdminPermitDetail;
    @Output() permitUpdated = new EventEmitter<void>();

    departments: PermitDepartment[] = [];
    private categories: PermitCategory[] = [];

    feeForm: FormGroup;

    savingFee = false;

    mapInitialized = false;
    isMapOpen = false;
    private map: any = null;
    private mapInitAttempts = 0;
    private readonly maxInitAttempts = 5;
    private readonly defaultCenter: [number, number] = [45.5017, -73.5673];
    private readonly defaultZoom = 13;

    uploading = false;
    selectedDocumentFieldId: string = OTHER_DOCUMENT_FIELD;
    readonly otherDocumentField = OTHER_DOCUMENT_FIELD;
    showFieldPicker = false;
    /** position: fixed, computed from the "+" tile's rect — escapes scrollable-ancestor clipping. */
    pickerPosition = { top: 0, left: 0 };

    @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;
    @ViewChild('addFileBtn') addFileBtnRef?: ElementRef<HTMLElement>;

    private destroy$ = new Subject<void>();

    constructor(
        private permitService: PermitV2Service,
        private departmentService: PermitDepartmentService,
        private categoryService: PermitCategoryService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.buildFeeForm();
        this.loadDepartments();
        this.loadCategories();
        this.loadApplicantProfile();
    }

    ngAfterViewInit(): void {
        // Map initializes on demand via toggleMap()
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.map) {
            try {
                this.map.remove();
            } catch (_) {}
            this.map = null;
            this.mapInitialized = false;
        }
    }

    // ─── Fee form ────────────────────────────────────────────────────────────

    // Snapshot of the last-saved values, so feeDirty can detect "back to where we started"
    // rather than just "was ever touched" (Angular's own dirty flag never clears on its own).
    private savedFeeState = { paid: false, paymentReference: '' };

    private buildFeeForm(): void {
        const paid = this.permit?.fees?.[0]?.paid ?? false;
        const paymentReference = this.permit?.fees?.[0]?.paymentReference ?? '';
        this.savedFeeState = { paid, paymentReference };
        this.feeForm = this.fb.group({
            paid: [paid],
            paymentReference: [paymentReference],
        });
    }

    /** True when the current values differ from what's actually saved — not just "touched". */
    get feeDirty(): boolean {
        if (!this.feeForm) return false;
        const { paid, paymentReference } = this.feeForm.value;
        return paid !== this.savedFeeState.paid || (paymentReference || '') !== this.savedFeeState.paymentReference;
    }

    get feesTotal(): number {
        return (this.permit?.fees || []).reduce((sum, fee) => sum + (fee.amount || 0), 0);
    }

    saveFee(): void {
        if (this.savingFee) return;
        const { paid, paymentReference } = this.feeForm.value;
        const payload: UpdateFeePayload = { paid, paymentReference: paymentReference || '' };
        this.savingFee = true;
        this.permitService
            .updateFee(this.permit._id, payload)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.savingFee = false;
                    this.feeForm.markAsPristine();
                    this.savedFeeState = { paid, paymentReference: paymentReference || '' };
                    this.messageService.success(this.translate.instant('permit.manage.common.saved'));
                    this.permitUpdated.emit();
                },
                // Leave the form dirty so the Save button stays available for a retry.
                error: () => {
                    this.savingFee = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    // ─── Department ──────────────────────────────────────────────────────────

    onDepartmentChange(departmentId: string): void {
        this.permitService
            .updateDepartment(this.permit._id, { department: departmentId })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.messageService.success(this.translate.instant('permit.manage.common.saved'));
                    this.permitUpdated.emit();
                },
                error: (err) => {
                    this.messageService.error(this.translate.instant(permitDepartmentErrorKey(err, 'permit.manage.common.error')));
                },
            });
    }

    loadDepartments(): void {
        this.departmentService
            .getDepartments({ includeInactive: false })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (departments) => {
                    this.departments = departments || [];
                },
                error: () => {
                    this.departments = [];
                },
            });
    }

    departmentLabel(department: PermitDepartment): string {
        return permitDepartmentName(department.name, this.translate.currentLang || 'fr');
    }

    // ─── Category + type ─────────────────────────────────────────────────────
    // Immutable: set at submission time and not editable from this panel.

    categoryLabel(category: PopulatedRef): string {
        return permitDepartmentName(category?.name, this.translate.currentLang || 'fr');
    }

    permitTypeLabel(permitType: PopulatedPermitTypeRef): string {
        return permitDepartmentName(permitType?.name, this.translate.currentLang || 'fr');
    }

    loadCategories(): void {
        this.categoryService
            .getCategories()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (categories) => {
                    this.categories = categories || [];
                },
                error: () => {
                    this.categories = [];
                },
            });
    }

    get categoryIconClass(): string {
        const category = this.categories.find((c) => c._id === this.permit?.category?._id);
        return category?.icon || 'icon-folder';
    }

    // ─── Applicant ───────────────────────────────────────────────────────────

    // The applicant record is often stored denormalized with empty name/contact fields
    // (e.g. self-applicants), carrying only a `user` id. Resolve the actual profile from
    // that id rather than trusting the embedded fields; fall back to embedded values for
    // applicants that have no linked user (e.g. manually-entered third parties).
    private applicantProfile: ProfileResponse | null = null;

    private loadApplicantProfile(): void {
        const userId = this.permit?.applicant?.user;
        if (!userId) return;
        this.userService
            .getProfileById(userId)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (profile) => {
                    this.applicantProfile = profile;
                },
                error: () => {
                    this.applicantProfile = null;
                },
            });
    }

    get applicantName(): string {
        const profile = this.applicantProfile;
        const profileName = profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : '';
        if (profileName) return profileName;
        const applicant = this.permit?.applicant;
        return `${applicant?.firstName || ''} ${applicant?.lastName || ''}`.trim();
    }

    get applicantEmail(): string {
        return this.applicantProfile?.email || this.permit?.applicant?.email || '';
    }

    get applicantPhone(): string {
        return this.applicantProfile?.phone || this.applicantProfile?.cellPhone || this.permit?.applicant?.phone || '';
    }

    get applicantPicture(): string {
        return this.applicantProfile?.picture || '';
    }

    getInitials(name: string): string {
        if (!name) return '';
        const parts = name.trim().split(' ');
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    // ─── Form data ───────────────────────────────────────────────────────────

    get formDataEntries(): { label: string; value: any }[] {
        if (!this.permit?.formData) return [];
        const formData = this.permit.formData;
        // The admin-detail response's permitType carries formFields/fieldUnits inline (no extra API
        // call needed), so real labels and units can be resolved directly from it.
        const fields = this.permit.permitType?.formFields || [];
        const fieldUnits = this.permit.permitType?.fieldUnits;
        return Object.keys(formData).map((key) => {
            const field = fields.find((f) => f.fieldId === key);
            if (!field) return { label: key, value: formData[key] };
            const lang = this.translate.currentLang || 'fr';
            const label = field.label?.[lang] || field.label?.fr || field.label?.en || key;
            const unit = field.fieldType === 'number' ? displayUnitFor(field, fieldUnits, lang) : undefined;
            return { label, value: unit ? `${formData[key]} ${unit}` : formData[key] };
        });
    }

    // formDataEntries returns new object references every cycle; without trackBy the file
    // links get torn down and rebuilt on every click, cancelling the navigation.
    trackFormDataEntry(_index: number, entry: { label: string; value: any }): string {
        return entry.label;
    }

    isFormDataFileUrl(value: any): boolean {
        return typeof value === 'string' && /^https?:\/\//i.test(value);
    }

    isFormDataFileUrlArray(value: any): boolean {
        return Array.isArray(value) && value.every((v) => this.isFormDataFileUrl(v));
    }

    formDataFileName(url: string): string {
        const match = [...(this.permit?.files || []), ...(this.permit?.internalFiles || [])].find((file) => file.url === url);
        if (match) return permitFileName(match);
        const segment = url.split('?')[0].split('/').pop();
        if (!segment) return url;
        try {
            return decodeURIComponent(segment);
        } catch (_) {
            return segment;
        }
    }

    // ─── Files ───────────────────────────────────────────────────────────────

    /** Filename to show for an attachment; the raw storage URL is kept for the link and tooltip. */
    fileName(file: PermitFile): string {
        return permitFileName(file);
    }

    openFile(file: PermitFile): void {
        const url = safePermitFileUrl(file);
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    isImage(file: PermitFile): boolean {
        return isPermitFileImage(file);
    }

    isDocumentPdf(file: PermitFile): boolean {
        return isPermitFilePdf(file);
    }

    isDocumentWord(file: PermitFile): boolean {
        return isPermitFileWord(file);
    }

    isDocumentExcel(file: PermitFile): boolean {
        return isPermitFileExcel(file);
    }

    /** File-type fields from the permit's form — offered as choices for which document is being added. */
    get documentFieldOptions(): FormFieldConfig[] {
        return (this.permit?.permitType?.formFields || []).filter((f) => f.fieldType === 'file');
    }

    documentFieldLabel(field: FormFieldConfig): string {
        const lang = this.translate.currentLang || 'fr';
        return field.label?.[lang] || field.label?.fr || field.label?.en || field.fieldId;
    }

    fileFieldCount(field: FormFieldConfig): number {
        const value = this.permit?.formData?.[field.fieldId];
        if (Array.isArray(value)) return value.length;
        return value ? 1 : 0;
    }

    isFileFieldFull(field: FormFieldConfig): boolean {
        return this.fileFieldCount(field) >= MAX_FILES_PER_FIELD;
    }

    onAddFileClick(): void {
        if (this.uploading) return;
        if (this.documentFieldOptions.length) {
            if (!this.showFieldPicker) {
                this.updatePickerPosition();
            }
            this.showFieldPicker = !this.showFieldPicker;
            return;
        }
        this.selectedDocumentFieldId = OTHER_DOCUMENT_FIELD;
        this.fileInputRef?.nativeElement.click();
    }

    // position: fixed — the tab pane's overflow-y: auto would clip a position: absolute popup.
    private updatePickerPosition(): void {
        const rect = this.addFileBtnRef?.nativeElement.getBoundingClientRect();
        if (!rect) return;
        this.pickerPosition = { top: rect.top, left: rect.right + 6 };
    }

    chooseDocumentField(fieldId: string, field?: FormFieldConfig): void {
        if (field && this.isFileFieldFull(field)) return;
        this.selectedDocumentFieldId = fieldId;
        this.showFieldPicker = false;
        this.fileInputRef?.nativeElement.click();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.showFieldPicker) return;
        const target = event.target;
        if (!(target instanceof Element) || !target.closest('.add-file-container')) {
            this.showFieldPicker = false;
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input?.files?.length) return;
        const file = input.files[0];
        this.uploadAndAttach(file);
        // reset so same file can be re-selected
        input.value = '';
    }

    private uploadAndAttach(file: File): void {
        const fieldId = this.selectedDocumentFieldId === OTHER_DOCUMENT_FIELD ? undefined : this.selectedDocumentFieldId;
        this.uploading = true;
        this.permitService
            .uploadTempFile(file)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (urls: string[]) => {
                    const url = Array.isArray(urls) ? urls[0] : (urls as any);
                    // Send the browser's file name alongside the URL — the upload only hands back a
                    // generated blob URL, so this is the one chance to keep the real name.
                    this.permitService
                        .attachFiles(this.permit._id, { files: [{ url, originalName: file.name, fieldId }] })
                        .pipe(take(1), takeUntil(this.destroy$))
                        .subscribe({
                            next: () => {
                                this.uploading = false;
                                this.messageService.success(this.translate.instant('permit.manage.common.saved'));
                                this.permitUpdated.emit();
                            },
                            error: () => {
                                this.uploading = false;
                                this.messageService.error(this.translate.instant('permit.manage.common.error'));
                            },
                        });
                },
                error: () => {
                    this.uploading = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    deleteFile(index: number): void {
        const confirmText = this.translate.instant('permit.manage.info.delete_file_confirm');
        const cancelLabel = this.translate.instant('permit.manage.common.cancel');
        const confirmLabel = this.translate.instant('permit.manage.common.delete');

        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: confirmText,
                    confirmLabel,
                    cancelLabel,
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((result) => {
                if (!result) return;
                this.permitService
                    .deleteFile(this.permit._id, index)
                    .pipe(take(1), takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.messageService.success(this.translate.instant('permit.manage.common.saved'));
                            this.permitUpdated.emit();
                        },
                        error: () => {
                            this.messageService.error(this.translate.instant('permit.manage.common.error'));
                        },
                    });
            });
    }

    // ─── Sync dot ────────────────────────────────────────────────────────────

    getSyncColor(syncStatus: string | null): string {
        const map: Record<string, string> = {
            [SyncStatus.SYNCED]: '#4caf50',
            [SyncStatus.SYNC_PENDING]: '#ff9800',
            [SyncStatus.SYNC_ERROR]: '#f44336',
        };
        return syncStatus ? map[syncStatus] || 'transparent' : 'transparent';
    }

    getSyncTooltip(syncStatus: string | null): string {
        const map: Record<string, string> = {
            [SyncStatus.SYNCED]: 'permit.manage.list.sync_synced',
            [SyncStatus.SYNC_PENDING]: 'permit.manage.list.sync_pending',
            [SyncStatus.SYNC_ERROR]: 'permit.manage.list.sync_error',
        };
        return syncStatus ? map[syncStatus] || '' : '';
    }

    // ─── Date helpers ────────────────────────────────────────────────────────

    formatDate(dateStr: string): string {
        if (!dateStr) return '';
        return moment(dateStr).format('DD MMM YYYY');
    }

    // ─── Leaflet mini-map ────────────────────────────────────────────────────

    hasValidLocation(): boolean {
        return !!this.permit?.location?.coordinates;
    }

    toggleMap(): void {
        this.isMapOpen = !this.isMapOpen;
        if (this.isMapOpen) {
            if (this.map) {
                this.map.remove();
                this.map = null;
                this.mapInitialized = false;
            }
            this.mapInitAttempts = 0;
            setTimeout(() => this.initMapWithRetry(), 50);
        } else if (this.map) {
            this.map.remove();
            this.map = null;
            this.mapInitialized = false;
        }
    }

    private initMapWithRetry(): void {
        if (this.initializeMap()) return;
        const retryInterval = setInterval(() => {
            this.mapInitAttempts++;
            if (this.mapInitialized || this.mapInitAttempts >= this.maxInitAttempts) {
                clearInterval(retryInterval);
                return;
            }
            this.initializeMap();
        }, 100);
    }

    private initializeMap(): boolean {
        if (this.mapInitialized && this.map) return true;

        const mapElement = document.getElementById('permit-detail-map');
        if (!mapElement) return false;

        try {
            if (this.map) this.map.remove();

            const coords = this.permit?.location?.coordinates;
            const initialCenter: [number, number] = coords ? [coords.lat, coords.lng] : this.defaultCenter;

            this.map = L.map('permit-detail-map', { zoomControl: false, dragging: false, scrollWheelZoom: false }).setView(initialCenter, this.defaultZoom);
            L.control.zoom({ position: 'topright' }).addTo(this.map);
            L.tileLayer(MAP_TILE_URL, {
                maxZoom: 18,
                minZoom: 10,
                attribution: MAP_TILE_ATTRIBUTION,
            }).addTo(this.map);

            if (coords) {
                L.marker([coords.lat, coords.lng], { icon: createLocationMarkerIcon() }).addTo(this.map);
            }

            this.map.invalidateSize();
            this.mapInitialized = true;
            this.cdr.detectChanges();
            return true;
        } catch (_) {
            return false;
        }
    }
}
